import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ProjectService } from '../../../core/project.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ProjectStructure, ResourcesOfEntity, Type } from '../../models/projects.model';
import { ProjectStructureClass } from '../../clases/project-structure.class';
import { NzFormatEmitEvent, NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { RequisitionService, RequisitionsItem } from '../../../core/requisition.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ItemRequisition } from '../../models/requisition.model';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-resources-tree-selector',
  standalone: false,
  templateUrl: './resources-tree-selector.component.html',
  styleUrl: './resources-tree-selector.component.scss'
})
export class ResourcesTreeSelectorComponent implements OnInit, OnDestroy {

  @Input() projectId: number = 0
  @Input() requisitionId: number = 0;
  @Input() canExceedQuantity: boolean = false;
  @Input() canEdit = true;

  destroy$: Subject<boolean> = new Subject<boolean>();
  tree?: ProjectStructureClass;
  displayedTree: NzTreeNodeOptions[] = [];
  resourcesTree: ProjectStructure[] = [];
  isLoading: boolean = true;
  step: 'visual' | 'tree' | 'resources' = 'visual';
  touchedResources = new Map<string, ResourcesOfEntity>();
  disabledAction: boolean = false;
  inputDebounce: any;
  inputRequeDebounce: any;
  optionVisualSelected: 'material' | 'tree' = 'tree';
  initialResources: ResourcesOfEntity[] = [];
  items: ItemRequisition[] = [];
  searchTerm: string = "";
  quantityDistribution: number = 0;
  distributingElements: ResourcesOfEntity[] = [];

  itemsKeySelected: Map<String, ResourcesOfEntity> = new Map<String, ResourcesOfEntity>();
  activeMassDeletion: boolean = false;
  isModalOpen: boolean = false;
  modalStep: 'tree' | 'resources' = 'tree';
  
  constructor(
    private projectService: ProjectService,
    private requisitionService: RequisitionService,
    private messageService: NzMessageService,
    private modal: NzModalService,
  ) { }

  ngOnInit(): void {

    this.projectService.getStructureOfAProject(
      this.projectId)
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((response) => {
          this.tree = new ProjectStructureClass(response);
          this.prepareInitialItems();
        }
      );
   
  }

  ngOnDestroy(): void {
    if (this.inputDebounce) {
      clearTimeout(this.inputDebounce);
    }
    this.destroy$.next(true);
  }

  onChangeTypeOfVisual(event: any) {
    this.optionVisualSelected = event;
    this.onChangeStep('visual');
  }

  onChangeStep(step: 'visual' | 'tree' | 'resources') {
    this.distributingElements = [];
    this.touchedResources = new Map<string, ResourcesOfEntity>();
    if(step == 'tree') {
      this.displayedTree = this.tree!.transformToNzTreeNode();
    } else if (step == 'visual') {
      // this.optionVisualSelected = 'material';
      this.prepareInitialItems();
    }

    this.step = step;
  }

  prepareInitialItems() {
      if(this.optionVisualSelected == 'material') this.displayedTree = [];
      this.isLoading = true;

      this.requisitionService.getRequisitionItems(this.requisitionId, this.optionVisualSelected == 'material')
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((data) => {
        this.isLoading = false;
        if(this.optionVisualSelected == 'material') {
          this.initialResources = data['Nodes'] ?? [];
          this.displayedTree = [];
        } else {
          this.items = data['items'];
          this.initialResources = data['Nodes'] ?? [];
          this.displayedTree = this.tree!.transformToNzTreeNode(this.touchedResources, this.tree?.buildResourcesTree(data));
        }
      });
  }

  handlerSearch() {
    if (this.inputDebounce) {
      clearTimeout(this.inputDebounce);
    }
    this.inputDebounce = setTimeout(() => {
      this.distributingElements = [];
      if (this.step == 'resources') {
        return this.findByNameResources(this.searchTerm);
      }

      return this.findByNameStructure(this.searchTerm);
    }, 500);
  }

  private findByNameResources(name: string) {
    if(!name) {
      this.displayedTree = this.tree!.transformToNzTreeNode(this.touchedResources, this.resourcesTree);
      return;
    }
    this.displayedTree = this.tree?.findPathByName(name, this.touchedResources, this.resourcesTree) ?? [];
  }

  private findByNameStructure(name: string) {
    if(!name) {
      this.displayedTree = this.tree!.transformToNzTreeNode(this.touchedResources);
      return;
    }
    this.displayedTree = this.tree?.findPathByName(name, this.touchedResources) ?? [];
  }

  onClickNode(event: NzFormatEmitEvent) {
    if (!event.node?.origin.isLeaf) {
      event.node!.isExpanded = !event.node!.isExpanded;
      return;
    }

    if(this.step == 'visual' || this.step == 'resources') {
      return;
    }
    

    this.isLoading = true;
    this.step = 'resources';
    this.searchTerm = "";

    this.projectService.getResourcesOfEntity(
      Number(event.node.origin['id']),
      'requisitions',
      [],
      []
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      this.displayedTree = this.tree!.transformToNzTreeNode(this.touchedResources, this.tree?.buildResourcesTree(response));
      this.resourcesTree = this.tree!.buildResourcesTree(response);
      this.isLoading = false;
    });
  }

  onCheckBoxAction(event: NzFormatEmitEvent) {

    if(this.step == 'resources') {
      const extractWBSResources = (node: any): any[] => {
        const resources: any[] = [];
        
        if (node.origin?.context === 'WBSResource') {
          resources.push(node.origin);
        }
        
        if (node.children) {
          node.children.forEach((child: any) => {
            resources.push(...extractWBSResources(child));
          });
        }
        
        return resources;
      };
  
      const allWBSResources: any[] = [];
      event.nodes?.forEach(node => {
        allWBSResources.push(...extractWBSResources(node));
      });
  
      const uniqueCodes = new Set(allWBSResources.map(r => r.code));
      const hasSingleType = uniqueCodes.size === 1;
  
      if(hasSingleType) {
        this.distributingElements = allWBSResources;
      } else {
        this.distributingElements = [];
      }
    } else if (this.step == 'visual') {
      this.itemsKeySelected.clear();
      for (const element of event.nodes ?? []) {
        this.itemsKeySelected.set(element.key, element.origin as any);
      }
      this.activeMassDeletion = this.itemsKeySelected.size > 0;
    }
  }

  onDistribution() {
    if(this.distributingElements.length <= 0 || !this.quantityDistribution) return;

    let remainingToDistribute = this.quantityDistribution;
    let totalDistributed = 0;

    for (const data of this.distributingElements) {
      if (remainingToDistribute <= 0) break;

      if (!data.WBSResourceNode) {
        data["hasError"] = "No data to do verifications";
        continue;
      }

      const availableQuantity = data.WBSResourceNode.budgetedQuantity - data.WBSResourceNode.consumedQuantity;
      const assignedQuantity = Math.min(remainingToDistribute, availableQuantity);

      if (assignedQuantity > 0) {
        data.WBSResourceNode.currentInputQuantity = Number(assignedQuantity.toFixed(4));
        data["hasError"] = undefined;
        this.onDoCalculations(data);
        this.touchedResources.set(data.key, data);
        totalDistributed += Number(assignedQuantity.toFixed(4));
        remainingToDistribute -= Number(assignedQuantity.toFixed(4));
      }
    }
  }

  private onDoCalculations(origin: ResourcesOfEntity, value: number = 0) {
    // Local changes
    const diferenceConsumed = (value ? value : origin.WBSResourceNode!.currentInputQuantity) - origin.WBSResourceNode!.quantity;
    const totalConsumendLocal = (origin.WBSResourceNode!.previouslyConsumedQuantity + (origin.WBSResourceNode!.quantity + diferenceConsumed));
    origin.WBSResourceNode!.currentConsumedQuantity = totalConsumendLocal;
    origin.WBSResourceNode!.currentRemainingQuantity = origin.WBSResourceNode!.budgetedQuantity - totalConsumendLocal;
    origin.WBSResourceNode!.currentInputPercentage = origin.WBSResourceNode!.currentInputQuantity / origin.WBSResourceNode!.budgetedQuantity;

    //Global changes
    const restConsumed = origin.WBSResourceNode!.consumedQuantity - (origin.WBSResourceNode!.quantity);
    const totalGlobal = (totalConsumendLocal + restConsumed);
    origin.WBSResourceNode!.totalConsumedQuantity = totalGlobal; 
    origin.WBSResourceNode!.totalRemainingQuantity = origin.WBSResourceNode!.budgetedQuantity - (totalGlobal);
    
    return origin.WBSResourceNode!.budgetedQuantity - (totalGlobal);

  }

  onBack() {
    this.searchTerm = "";
    if(this.step == 'tree') {
      this.onChangeStep('visual');
    } else if (this.step === 'resources') {
      this.onChangeStep('tree');
    }
  }

  openTreeModal() {
    this.isModalOpen = true;
    this.modalStep = 'tree';
    this.searchTerm = "";
    this.touchedResources = new Map<string, ResourcesOfEntity>();
    this.distributingElements = [];
    this.displayedTree = this.tree!.transformToNzTreeNode();
  }

  closeTreeModal() {
    this.isModalOpen = false;
    this.modalStep = 'tree';
    this.searchTerm = "";
    this.touchedResources = new Map<string, ResourcesOfEntity>();
    this.distributingElements = [];
    this.displayedTree = [];
    // Restore visual state
    this.step = 'visual';
    this.prepareInitialItems();
  }

  onModalBack() {
    this.searchTerm = "";
    if (this.modalStep === 'resources') {
      this.modalStep = 'tree';
      this.distributingElements = [];
      this.touchedResources = new Map<string, ResourcesOfEntity>();
      this.displayedTree = this.tree!.transformToNzTreeNode();
    }
  }

  onModalClickNode(event: NzFormatEmitEvent) {
    if (!event.node?.origin.isLeaf) {
      event.node!.isExpanded = !event.node!.isExpanded;
      return;
    }

    if (this.modalStep == 'resources') {
      return;
    }

    this.isLoading = true;
    this.modalStep = 'resources';
    this.searchTerm = "";

    this.projectService.getResourcesOfEntity(
      Number(event.node.origin['id']),
      'requisitions',
      [],
      []
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      this.displayedTree = this.tree!.transformToNzTreeNode(this.touchedResources, this.tree?.buildResourcesTree(response));
      this.resourcesTree = this.tree!.buildResourcesTree(response);
      this.isLoading = false;
    });
  }

  onAddFromModal() {
    this.disabledAction = true;
    this.isLoading = true;
    const auxItems: any[] = [];
    for (const element of this.touchedResources.values()) {
      if (element.context == "WBSResource") {
        auxItems.push({
          componentResourceId: element.componentResourceId,
          productionUnitId: element.productionUnitId ? element.productionUnitId : null,
          quantity: element.WBSResourceNode?.currentInputQuantity ?? 0,
          notes: element["notes"] ?? ""
        });
      }
    }

    auxItems.forEach(item => {
      Object.keys(item).forEach(key => {
        if (item[key as keyof RequisitionsItem] === undefined) {
          delete item[key as keyof RequisitionsItem];
        }
      });
    });

    if (auxItems.length == 0) {
      this.messageService.create('error', 'No hay datos disponibles para realizar operación');
      this.isLoading = false;
      this.disabledAction = false;
      return;
    }

    this.requisitionService.CUDItemToRequisition(
      this.requisitionId, 'add', auxItems)
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((response) => {
        this.isLoading = false;
        if (response.status) {
          this.messageService.create('success', `¡Listo! 🎉 Los recursos fueron agregados con éxito a la requisición.`);
        } else {
          this.messageService.create('error', response.message);
        }
        this.disabledAction = false;
        this.isModalOpen = false;
        this.modalStep = 'tree';
        this.displayedTree = [];
        this.touchedResources = new Map<string, ResourcesOfEntity>();
        this.step = 'visual';
        this.prepareInitialItems();
      });
  }


  isMaterialOrMachinery(data: any) {
    return data.type === 'Material' || data.type === 'Machinery';
  }

  onAddNoteToMaterial(event: any, data: ResourcesOfEntity) {
    const note = event.target.value;
    if (note) {
      data["notes"] = note;
      this.touchedResources.set(data.key, data);
    }
  }

  onValidateResource(val: number, data: ResourcesOfEntity) {
    if (Number.isNaN(val)) {
        // Error number is Nan
        // data["hasError"] = "Value is not a valid one";
        this.touchedResources.delete(data.key);
        return false;
      }

      if (!data.WBSResourceNode) {
        // Error no data exist
        // data["hasError"] = "No data to do verifications";
        this.touchedResources.delete(data.key);
        return false;
      }

      if (val < 0) {
        // Error val is bigger than remaining quantity
        // data["hasError"] = "value has to be more than 0";
        this.touchedResources.delete(data.key);
        return false;
      }

      if(val == 0) {
        data["hasError"] = undefined;
        data.WBSResourceNode.currentInputQuantity = val;
        
        if(this.step == 'resources')
        this.touchedResources.delete(data.key);
        return true;
      }

      // const validation = this.materialSurpaseQuantity(val, data);
      const validation = this.onDoCalculations(data, val);

      // Validate if user can surpase
      if (validation < 0 && !this.canExceedQuantity) {
        // Error val is bigger than remaining quantity
        // data["hasError"] = "Value is bigger than remaining quantity";
        data.WBSResourceNode.currentInputQuantity = val;
        this.touchedResources.delete(data.key);
        return false;
      }

      return true;
  }

  materialSurpaseQuantity(val: number, data: ResourcesOfEntity): number {

    //const validation = Number((data.WBSResourceNode!.budgetedQuantity - (val + data.WBSResourceNode!.consumedQuantity)).toFixed(4));
    const validation = this.onDoCalculations(data, val);

    return validation;
  }

  onMaterialChangeValue(event: any, data: ResourcesOfEntity) {
    if(this.inputDebounce) clearTimeout(this.inputDebounce);
      this.inputDebounce = setTimeout(() => {
        const val = Number(event.target.value);
        const isValid = this.onValidateResource(val, data);
        if(isValid && val >= 0) {
          data.WBSResourceNode!.currentInputQuantity = val;
          data["hasError"] = undefined;
          if(val !=0 || (this.step == 'visual' && val == 0)) {
            this.onDoCalculations(data);
            this.touchedResources.set(data.key, data);
          }
        }
    }, 200);
  }

  getClassInput(origin: ResourcesOfEntity) {
    if (!origin.WBSResourceNode) {
      return 'full';
    }

    if(!origin.WBSResourceNode!.currentInputQuantity) {
      return 'full';
    }

    const val = this.onDoCalculations(origin);
   //  const val = this.materialSurpaseQuantity(origin.WBSResourceNode!.currentInputQuantity, origin);
    if (val >= 0) {
      return 'available';
    } else {
      return 'surpase';
    } 
  }

  getClass(origin: ResourcesOfEntity) {
    if (!origin.WBSResourceNode) {
      return 'full';
    }

    const val = this.onDoCalculations(origin);
    // const val = this.materialSurpaseQuantity(origin.WBSResourceNode!.currentInputQuantity, origin);

    if (val > 0) {
      return 'available';
    } else if (val == 0) {
      return 'full';
    } else {
      return 'surpase';
    }
  }

  onHandlerResourcesAction(mode: 'add' | 'edit' = 'add') {
    this.disabledAction = true;
    this.isLoading = true;
    const auxItems: any[] = [];
    for (const element of this.touchedResources.values()) {
      if(element.context == "WBSResource") {
        auxItems.push({
          componentResourceId: element.componentResourceId,
          productionUnitId: element.productionUnitId ? element.productionUnitId : null,
          quantity: element.WBSResourceNode?.currentInputQuantity ?? 0,
          notes: element["notes"] ?? ""
        });
      } else if (element.context == "RequisitionItem") {
        const item = this.items.find(x => x.itemId == element.id);
        if(item) {
          auxItems.push({
            itemId: item.itemId,
            componentResourceId: item.componentResourceId,
            productionUnitId: item.productionUnitId ? element.productionUnitId : null,
            quantity: element.WBSResourceNode?.currentInputQuantity ?? 0,
            notes: element["notes"] ?? ""
          });
        }
      }
    }

    auxItems.forEach(item => {
      Object.keys(item).forEach(key => {
        if (item[key as keyof RequisitionsItem] === undefined) {
          delete item[key as keyof RequisitionsItem];
        }
      });
    });

    if(auxItems.length == 0) {
      this.messageService.create('error', 'No hay datos disponibles para realizar operación');
      this.isLoading = false;
      this.onChangeStep('visual');
      return;
    }

    if(mode == 'add') {
      this.requisitionService.CUDItemToRequisition(
      this.requisitionId, mode, auxItems)
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((response) => {
        this.isLoading = false;
          if(response.status) {
            this.messageService.create('success', `¡Listo! 🎉 Los recursos fueron agregados con éxito a la requisición.`);
          } else {
            // Error happend handler
            this.messageService.create('error', response.message);
          }
          this.disabledAction = false;
          this.displayedTree = [];
          this.onChangeStep('visual');
        });
    } else {
      const requests = auxItems.map(item => 
        this.requisitionService.CUDItemToRequisition(item.itemId, mode, {...item})
          .pipe(takeUntil(this.destroy$))
      );

      Promise.all(requests.map(req => firstValueFrom(req)))
        .then(() => {
          this.isLoading = false;
          // this.messageService.create('success', '¡Listo! 🎉 Los recursos fueron actualizados con éxito a la requisición.');
          this.disabledAction = false;
          this.displayedTree = [];
          this.onChangeStep('visual');
        })
        .catch((error) => {
          this.isLoading = false;
          // this.messageService.create('error', 'Error al actualizar los recursos');
          this.disabledAction = false;
          this.onChangeStep('visual');
        });
    }
  }

  onDeleteItem(origin: any, ignoringUpdate = false) {
    if(!ignoringUpdate) {

      this.isLoading = true;
    }

    return new Promise<boolean>((resolve, reject) => {

      this.requisitionService.CUDItemToRequisition(
        origin.id, 'delete')
        .pipe(
          takeUntil(this.destroy$)
        ).subscribe((response) => {
          resolve(true);
          if(!ignoringUpdate) {
            
            this.isLoading = false;
              if(response.status) {
                this.messageService.create('success', `¡Listo! 🎉 El recurso fue eliminado con éxito de la requisición.`);
              } else {
                // Error happend handler
                this.messageService.create('error', response.message);
              }
              this.disabledAction = false;
              this.displayedTree = [];
              this.onChangeStep('visual');
          }
          });
    })
  }

  async onDeleteMassAction() {
      if(this.itemsKeySelected.size == 0) {
        return;
      }
      const ref = this.modal.create({
        nzTitle: "Eliminando recursos",
        nzContent: "Espere un momento mientras se ejecuta la acción...",
        nzFooter: null,
        nzCentered: true,
      });
      // NOTES
      // if the type is "Activity" means all "Material" below that activity needs to be deleted.
      // if the type is "Project" means all "Activity" below needs to be deleted
      let deletedMaterials : ResourcesOfEntity[] = [];
      for (const element of this.itemsKeySelected) {
        console.log(element[1], 'element');
        if(element[1].type == Type.Project) {
          const allActivities = this.getAllActivities();
          console.log(allActivities, 'allActivities');
          for (const activity of allActivities) {
            const allMaterials = this.getAllMaterialsPerActivityCode(activity.key);
            console.log(allMaterials, 'allActiallMaterialsvities');
            deletedMaterials = [...deletedMaterials, ...allMaterials, activity];
          }
        } else if(element[1].type == Type.Activity) {
          const allMaterials = this.getAllMaterialsPerActivityCode(element[1].key);
          deletedMaterials = [...deletedMaterials, ...allMaterials, element[1]];
        } else if(element[1].type == Type.Material || element[1].type == Type.Machinery) {
          deletedMaterials = [...deletedMaterials, element[1]];
        }
      }
  
      await this.deleteAllMaterials([...deletedMaterials]);
      ref.close();
      this.itemsKeySelected.clear();
      this.isLoading = false;
      this.messageService.create('success', `¡Listo! 🎉 Los recursos fueron eliminados con éxito de la requisición.`);   
      this.disabledAction = false;
      this.displayedTree = [];
      this.activeMassDeletion = false;
      this.onChangeStep('visual');
      
    }
  
    private async deleteAllMaterials(materials: ResourcesOfEntity[]) {
      for (const material of materials) {
        await this.onDeleteItem(material, true);
      }
  
    }
  
    private getAllActivities() {
      console.log(this.initialResources);
      return this.initialResources.filter(x => x.type == 'Activity');
    }
  
    private getAllMaterialsPerActivityCode(code: string) {
      return this.initialResources.filter(x => (x.type == Type.Material && x.parentKey == code) 
      || (x.type == Type.Machinery && x.parentKey == code));
    }

}
